import { Component, effect, inject, input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { QuillViewComponent } from 'ngx-quill';
import type Quill from 'quill';
import { Subscription } from 'rxjs';
import { LinkController } from 'src/app/domain/controllers/link.controller';
import { DATA_TAG_CONTEXT } from '../ra-editor/data-tag-context.token';
import {
    refreshDataTagValidityInRoot,
    syncDataTagDisplay,
} from '../ra-editor/data-tag-display.sync';
import { registerDataTagBlot } from '../ra-editor/quill-data-tag';
import { registerSpanStyleFormats } from '../ra-editor/quill-span-style';
import { registerLinkPlaceholderBlot } from '../ra-editor/quill-link-placeholder';

@Component({
    selector: 'ra-viewer-text',
    templateUrl: 'ra-viewer-text.component.html',
    styleUrl: 'ra-viewer-text.component.scss',
    imports: [QuillViewComponent],
})
export class RaViewerTextComponent implements OnInit, OnDestroy {
    private _linkController = inject(LinkController);
    private _translate = inject(TranslateService);
    private _dataTagContext = inject(DATA_TAG_CONTEXT, { optional: true });
    private _langChangeSub?: Subscription;

    public content = input.required<string | Record<string, unknown>>();

    /** Fonte aplicada ao texto (documento → empresa → padrão, resolvida pelo pai). */
    public fontFamily = input<string | undefined>();

    /** Conteúdo normalizado (sempre string JSON) para o quill-view. */
    public contentJson = '';
    private quill: Quill | null = null;

    constructor() {
        registerLinkPlaceholderBlot();
        registerDataTagBlot();
        registerSpanStyleFormats();

        effect(() => {
            this._dataTagContext?.();
            this._linkController.links();
            this._syncDataTagDisplay();
            if (this.quill) {
                setTimeout(() => this._refreshTagValidity(), 0);
            }
        });

        effect(() => {
            const raw = this.content();
            this.contentJson =
                raw == null || raw === ''
                    ? ''
                    : typeof raw === 'string'
                      ? raw
                      : (JSON.stringify(raw) ?? '');
            this._applyContent();
        });
    }

    ngOnInit(): void {
        this._syncDataTagDisplay();
        this._langChangeSub = this._translate.onLangChange.subscribe(() => {
            this._syncDataTagDisplay();
            if (this.quill && this.contentJson?.trim()) {
                setTimeout(() => this._applyContent(), 0);
            }
        });
    }

    ngOnDestroy(): void {
        this._langChangeSub?.unsubscribe();
    }

    /**
     * Cria o editor Quill e aplica o conteúdo.
     * @param editor - O editor Quill criado.
     */
    public onEditorCreated(editor: Quill): void {
        this.quill = editor;
        this._optOutOfTextRewriters(editor);
        this._syncDataTagDisplay();
        this._applyContent();
        this._refreshTagValidity();
    }

    /**
     * Marca o conteúdo como intraduzível para tradutores automáticos (Google
     * Tradutor do Chrome, Grammarly e afins). Mesma blindagem que o `ra-editor`
     * já faz no editor — sem ela o texto SOME no preview.
     *
     * O tradutor troca os nós de texto por `<font>`; o MutationObserver do Quill
     * enxerga DOM que não corresponde ao Delta interno, normaliza de volta e
     * remove o que não é blot conhecido. Resultado: cards vazios, sem erro no
     * console, só na máquina de quem tem a tradução automática ligada.
     */
    private _optOutOfTextRewriters(editor: Quill): void {
        editor.root.setAttribute('translate', 'no');
        editor.root.classList.add('notranslate');
        editor.root.setAttribute('data-gramm', 'false');
        editor.root.setAttribute('data-gramm_editor', 'false');
        editor.root.setAttribute('data-enable-grammarly', 'false');
    }

    private _syncDataTagDisplay(): void {
        syncDataTagDisplay({
            translate: this._translate,
            links: this._linkController.links(),
            contextSignal: this._dataTagContext ?? undefined,
            findLinkByTag: tag =>
                this._linkController
                    .links()
                    .find(l => (l.value ?? l.name ?? '').replace(/^\$/, '').trim() === tag),
        });
    }

    private _refreshTagValidity(): void {
        if (!this.quill) return;
        refreshDataTagValidityInRoot(this.quill.root);
    }

    /**
     * Aplica o Delta com updateContents (texto, listas e tabelas) — quill-view usa setContents por padrão e tabelas não aparecem.
     */
    private _applyContent(): void {
        if (!this.quill || !this.contentJson?.trim()) return;

        try {
            const delta = JSON.parse(this.contentJson) as { ops?: unknown[] };
            const ops = delta?.ops;
            if (!Array.isArray(ops) || ops.length === 0) return;

            this.quill.updateContents(
                [{ delete: this.quill.getLength() }, ...ops] as never,
                'silent'
            );
        } catch {
            return;
        }
    }
}
